import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import {
    Sparkles,
    ShieldCheck,
    Clock,
    UserRound,
    Send,
    ChevronDown,
    CheckCircle2,
} from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import { useI18n } from "../i18n/I18nContext";
import { api, formatApiError } from "../lib/api";
import { useSearchParams } from "react-router-dom";

const EMPTY = {
    name: "",
    email: "",
    phone: "",
    whatsapp: "",
    type: "info",
    property_id: "",
    property_title: "",
    property_type: "",
    transaction: "",
    city: "",
    budget: "",
    timeline: "",
    message: "",
};

export default function Inquiry() {
    const { t } = useI18n();
    const [params] = useSearchParams();
    const [form, setForm] = useState({
        ...EMPTY,
        property_id: params.get("property_id") || "",
        property_title: params.get("property_title") || "",
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const { scrollY } = useScroll();
    const heroY = useTransform(scrollY, [0, 400], [0, -60]);
    const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.35]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const payload = {
                ...form,
                budget: form.budget === "" ? null : Number(form.budget),
                property_id: form.property_id || null,
                property_title: form.property_title || null,
                property_type: form.property_type || null,
                transaction: form.transaction || null,
                city: form.city || null,
                timeline: form.timeline || null,
            };
            await api.post("/inquiries", payload);
            toast.success(t("inquiry.success_title"));
            setSent(true);
        } catch (err) {
            toast.error(formatApiError(err) || t("inquiry.error"));
        } finally {
            setSending(false);
        }
    };

    return (
        <PublicLayout>
            {/* KINETIC HERO */}
            <section className="relative bg-brand-navy text-white overflow-hidden" data-testid="inquiry-hero">
                <div className="absolute inset-0 opacity-25 bg-grain" aria-hidden />
                {/* Slow editorial marquee */}
                <div className="absolute top-24 lg:top-32 left-0 right-0 overflow-hidden text-brand-gold/10 pointer-events-none select-none">
                    <motion.div
                        animate={{ x: [0, -1200] }}
                        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                        className="whitespace-nowrap font-display text-[10rem] leading-none tracking-tighter"
                    >
                        Devis · Information · Confidentiel · Devis · Information · Confidentiel ·
                    </motion.div>
                </div>

                <motion.div
                    style={{ y: heroY, opacity: heroOpacity }}
                    className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36 grid lg:grid-cols-12 gap-10"
                >
                    <div className="lg:col-span-8">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            className="inline-flex items-center gap-3 text-brand-gold text-[0.7rem] tracking-[0.32em] uppercase font-semibold mb-6"
                        >
                            <span className="w-10 h-px bg-brand-gold" />
                            <Sparkles className="w-4 h-4" /> {t("inquiry.hero_kicker")}
                        </motion.div>

                        <MaskedTitle
                            lines={[t("inquiry.hero_title_a"), t("inquiry.hero_title_b")]}
                        />

                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.6 }}
                            className="mt-8 text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed"
                        >
                            {t("inquiry.hero_desc")}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.9 }}
                            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/85"
                        >
                            <Promise icon={ShieldCheck} label={t("inquiry.promise_1")} />
                            <Promise icon={Clock} label={t("inquiry.promise_2")} />
                            <Promise icon={UserRound} label={t("inquiry.promise_3")} />
                        </motion.div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4, duration: 0.8 }}
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 text-brand-gold/70 text-[0.65rem] tracking-[0.35em] uppercase font-semibold flex flex-col items-center gap-1"
                >
                    scroll
                    <ChevronDown className="w-4 h-4 animate-bounce" />
                </motion.div>
            </section>

            {/* FORM SECTION */}
            <section className="relative py-24 sm:py-32 bg-brand-paper">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {sent ? (
                        <SuccessBlock t={t} />
                    ) : (
                        <form onSubmit={submit} className="space-y-16" data-testid="inquiry-form">
                            <Chapter
                                number="01"
                                title={t("inquiry.chapter_1")}
                            >
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <Field label={t("inquiry.name")} value={form.name} onChange={(v) => setField("name", v)} required testid="inq-name" />
                                    <Field label={t("inquiry.email")} type="email" value={form.email} onChange={(v) => setField("email", v)} required testid="inq-email" />
                                    <Field label={t("inquiry.phone")} value={form.phone} onChange={(v) => setField("phone", v)} testid="inq-phone" />
                                    <Field label={t("inquiry.whatsapp")} value={form.whatsapp} onChange={(v) => setField("whatsapp", v)} testid="inq-whatsapp" />
                                </div>
                            </Chapter>

                            <Chapter number="02" title={t("inquiry.chapter_2")}>
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <SegmentedField
                                        label={t("inquiry.type")}
                                        value={form.type}
                                        onChange={(v) => setField("type", v)}
                                        testid="inq-type"
                                        options={[
                                            { v: "info", l: t("inquiry.type_info") },
                                            { v: "quote", l: t("inquiry.type_quote") },
                                        ]}
                                    />
                                    <Field
                                        label={t("inquiry.property_ref")}
                                        value={form.property_title}
                                        onChange={(v) => setField("property_title", v)}
                                        placeholder={t("inquiry.property_ref_placeholder")}
                                        testid="inq-property-title"
                                    />
                                </div>
                            </Chapter>

                            <Chapter number="03" title={t("inquiry.chapter_3")}>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <Select
                                        label={t("inquiry.property_type")}
                                        value={form.property_type}
                                        onChange={(v) => setField("property_type", v)}
                                        testid="inq-property-type"
                                        options={[
                                            { v: "", l: "—" },
                                            { v: "villa", l: t("properties.filters.villa") },
                                            { v: "appartement", l: t("properties.filters.appartement") },
                                            { v: "maison", l: t("properties.filters.maison") },
                                            { v: "terrain", l: t("properties.filters.terrain") },
                                        ]}
                                    />
                                    <Select
                                        label={t("inquiry.transaction")}
                                        value={form.transaction}
                                        onChange={(v) => setField("transaction", v)}
                                        testid="inq-transaction"
                                        options={[
                                            { v: "", l: "—" },
                                            { v: "sale", l: t("inquiry.transaction_sale") },
                                            { v: "rent", l: t("inquiry.transaction_rent") },
                                        ]}
                                    />
                                    <Field label={t("inquiry.city")} value={form.city} onChange={(v) => setField("city", v)} testid="inq-city" />
                                    <Field label={t("inquiry.budget")} type="number" value={form.budget} onChange={(v) => setField("budget", v)} testid="inq-budget" />
                                    <Select
                                        label={t("inquiry.timeline")}
                                        value={form.timeline}
                                        onChange={(v) => setField("timeline", v)}
                                        testid="inq-timeline"
                                        options={[
                                            { v: "", l: "—" },
                                            { v: "immediate", l: t("inquiry.timeline_immediate") },
                                            { v: "1-3m", l: t("inquiry.timeline_1_3") },
                                            { v: "3-6m", l: t("inquiry.timeline_3_6") },
                                            { v: "6m+", l: t("inquiry.timeline_6plus") },
                                        ]}
                                    />
                                </div>
                            </Chapter>

                            <Chapter number="04" title={t("inquiry.chapter_4")}>
                                <Field
                                    label={t("inquiry.message")}
                                    value={form.message}
                                    onChange={(v) => setField("message", v)}
                                    required
                                    textarea
                                    placeholder={t("inquiry.message_placeholder")}
                                    testid="inq-message"
                                />
                                <div className="pt-8">
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        data-testid="inq-submit"
                                        className="group relative inline-flex items-center gap-3 px-10 py-5 bg-brand-navy hover:bg-brand-gold text-white text-[0.72rem] tracking-[0.28em] uppercase font-semibold transition-colors disabled:opacity-60"
                                    >
                                        <Send className="w-4 h-4" />
                                        {sending ? t("inquiry.submitting") : t("inquiry.submit")}
                                    </button>
                                </div>
                            </Chapter>
                        </form>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}

function MaskedTitle({ lines }) {
    return (
        <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] font-semibold tracking-tight">
            {lines.map((line, i) => (
                <span key={i} className="block overflow-hidden">
                    <motion.span
                        initial={{ y: "110%" }}
                        animate={{ y: 0 }}
                        transition={{ duration: 0.9, delay: 0.15 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
                        className={`inline-block ${i === 1 ? "italic text-brand-gold" : ""}`}
                    >
                        {line}
                    </motion.span>
                </span>
            ))}
        </h1>
    );
}

function Promise({ icon: Icon, label }) {
    return (
        <span className="inline-flex items-center gap-2">
            <Icon className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
            <span className="text-[0.72rem] tracking-[0.22em] uppercase font-semibold">{label}</span>
        </span>
    );
}

function Chapter({ number, title, children }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
        >
            <div className="flex items-baseline gap-6 mb-10">
                <span className="font-display text-6xl text-brand-gold/60">{number}</span>
                <div className="flex-1 border-b border-brand-navy/15" />
            </div>
            <h3 className="font-display text-3xl text-brand-navy mb-8">{title}</h3>
            {children}
        </motion.div>
    );
}

function Field({ label, value, onChange, required, type = "text", textarea, testid, placeholder }) {
    const cls =
        "w-full bg-transparent border-0 border-b border-brand-navy/25 focus:border-brand-gold focus:outline-none py-3 text-base text-brand-navy placeholder:text-brand-navy/30 transition-colors";
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-brand-navy/70">
                {label}
                {required ? <span className="text-brand-gold"> *</span> : null}
            </span>
            <div className="mt-1">
                {textarea ? (
                    <textarea
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        rows={4}
                        required={required}
                        placeholder={placeholder}
                        data-testid={testid}
                        className={cls + " resize-y min-h-[120px]"}
                    />
                ) : (
                    <input
                        type={type}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        required={required}
                        placeholder={placeholder}
                        data-testid={testid}
                        className={cls}
                    />
                )}
            </div>
        </label>
    );
}

function Select({ label, value, onChange, options, testid }) {
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-brand-navy/70">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full mt-1 bg-transparent border-0 border-b border-brand-navy/25 focus:border-brand-gold focus:outline-none py-3 text-base text-brand-navy transition-colors"
            >
                {options.map((o) => (
                    <option key={o.v} value={o.v}>
                        {o.l}
                    </option>
                ))}
            </select>
        </label>
    );
}

function SegmentedField({ label, value, onChange, options, testid }) {
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-brand-navy/70">{label}</span>
            <div className="mt-3 grid grid-cols-2 gap-2" data-testid={testid}>
                {options.map((o) => (
                    <button
                        type="button"
                        key={o.v}
                        onClick={() => onChange(o.v)}
                        data-testid={`${testid}-${o.v}`}
                        className={`px-4 py-3 text-[0.72rem] tracking-[0.2em] uppercase font-semibold transition-all border ${
                            value === o.v
                                ? "bg-brand-navy text-white border-brand-navy"
                                : "bg-transparent text-brand-navy border-brand-navy/25 hover:border-brand-gold hover:text-brand-gold"
                        }`}
                    >
                        {o.l}
                    </button>
                ))}
            </div>
        </label>
    );
}

function SuccessBlock({ t }) {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            data-testid="inquiry-success"
            className="bg-white border border-brand-emerald/20 p-14 text-center"
        >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-brand-emerald/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-brand-emerald" strokeWidth={1.5} />
            </div>
            <h2 className="font-display text-4xl text-brand-navy mb-3">{t("inquiry.success_title")}</h2>
            <p className="text-gray-600 max-w-md mx-auto">{t("inquiry.success_desc")}</p>
        </motion.div>
    );
}
